import AnnotationController from "../annotation/controller";
import EnvironmentController from "../EnvironmentFeature/controller";
import SceneStateService from "./service";

const XML2JS = require('xml2js');
const stateCurrentVersion = 2.0;

class SceneStateController {
    constructor() {
        this.doc = "";
        this.root = "";
        this.features = [];
        this.caches = [];
        this._initFeatureFunctions();
    }

    _init() {
        this.doc = document.implementation.createDocument("", "", null);
        this.root = this.doc.createElement("state");
        this.root.setAttribute("version", "2");
        this._initCaches();
    }

    _initFeatureFunctions() {
        this.features.push(modelManager);
        this.features.push(HelperObjectManager);
        this.features.push(ARMarkerManager);
        this.features.push(selectionFeatureManager);
        this.features.push(catchVolumeFeatureManager);
        this.features.push(imageHotSpotFeature);
        this.features.push(videoHotSpotFeature);
        this.features.push(animationController);
        this.features.push(AnnotationController);
        this.features.push(cableFeatureController);
        this.features.push(pointCloudController);
        this.features.push(drawingFeatureController);
        this.features.push(EnvironmentController);
        this.features.push(explosionManager);
        this.features.push(floor);
        this.features.push(informationAnnotationFeature);
        this.features.push(measurementController);
        this.features.push(MediaScreenManager);
        this.features.push(MeshHierarchyViewFeatureManager);
        this.features.push(SessionPlaybackConfigurator);
        this.features.push(ParticipantService);
        this.features.push(clipPlaneController);
        this.features.push(StepInstructionScreenManager);
    }

    _initCaches() {
        this.caches = [];

        modelManager._modelList.forEach(model => {
            this.caches.push({
                cacheId: model.id,
                coreId: model.cacheFileDetails.core.id,
                type: model.type,
                name: model.name
            })
        });
    }

    _getXmlData() {
        return this.root.outerHTML;
    }

    _createStateNode(document, root, stateName, modelId = null) {
        const stateData = document.createElement("stateData");
        stateData.setAttribute("version", stateGlobalxmlVersion);
        stateData.setAttribute("stateName", stateName);
        stateData.setAttribute("modelId", modelId);
        root.appendChild(stateData);
    }

    async _loadFeaturesFromXml(xmlData, sceneStateModels, arMarkers = null, shouldApplyModelMatrix = true) {
        const parser = new XML2JS.Parser();

        parser.parseString(xmlData, async (error, xmlObj) => {
            if (error) {
                console.log("Error on parsing XML data: ", error);
            } else {
                const state = xmlObj.state;
                if (!sceneStateModels) {
                    sceneStateModels = [];
                    for (const modelNode of state.modelList) {
                        for (const modelInfo of modelNode.Model || []) {
                            const model = modelManager.getModelById(modelInfo?.$?.id);
                            if (model) {
                                sceneStateModels.push(model);
                            }
                        }
                    }
                }

                for (const feature of this.features) {
                    // ARMarkerManager contains the "addMarkerMesh"
                    let sceneStateModelsParam = [...sceneStateModels];
                    if (feature.addMarkerMesh) {
                        if (!arMarkers) {
                            arMarkers = [];
                            if (state.markerList) {
                                for (const markerNode of state.markerList) {
                                    if (!markerNode.ARMarker) {
                                        continue;
                                    }

                                    for (const markerInfo of markerNode.ARMarker) {
                                        arMarkers.push({
                                            id: markerInfo?.$?.id,
                                            name: markerInfo?.$?.name,
                                            visible: markerInfo?.$?.isVisible === "1",
                                            matrix: markerInfo.matrix[0].value.map(value => parseFloat(value))
                                        });
                                    }
                                }
                            }

                        }
                        sceneStateModelsParam = arMarkers;
                    }
                    state.shouldApplyModelMatrix = shouldApplyModelMatrix;
                    await feature.loadState(state, sceneStateModelsParam);
                }
            }
        });
    }

    async updateModelStateListOnMenu(modelId, modelName) {
        let modelStateList = await SceneStateService.getModelStateList({
            modelId: modelId
        });

        modelStateList = modelStateList.map(d => d.split(".xml")[0]);
        setModelStatesArray([modelId, modelName, ...modelStateList]);
    }

    async saveModelState(modelId, modelStateName) {
        this.saveState(null, modelId, modelStateName);
        const filteredModel = modelManager._modelList.filter(model => model.id === modelId)[0];

        try {
            await SceneStateService.createModelState({
                xmlString: this._getXmlData(),
                modelId: filteredModel?.id,
                modelStateName: modelStateName
            });

            const vrText = "Model state saved successfully";
            VRMessageController.addMessage({text: vrText, priority: VrMessagePriorityEnum.LOWPRIORITYINFO});

            await this.updateModelStateListOnMenu(modelId, filteredModel?.name);

        } catch (error) {
            console.error(error);
        }
    }

    saveState(sceneStateId = null, modelId = null, modelStateName = null) {
        this._init();
        let self = this;
        this._createStateNode(self.doc, self.root, sceneStateId || modelStateName, modelId);
        self.features.forEach(feature => {
            feature.saveState(self.doc, self.root, modelId);
        });

        return {
            xmlString: self._getXmlData(),
            caches: self.caches
        };
    }

    async loadState(sceneStateId, stateName, sceneStateModels = null, sceneStateXML = null, arMarkers = null, menuEnabled = true) {
        wv.trainingAuthoringEnabled = false;
        wv.effectController.meetingInfo.haveLicenseForServerlessWorkspace = menuEnabled;
        if (!menuEnabled) {
            wv.effectController.meetingInfo.data.menuEnabled = menuEnabled;
            disableMenuForLicense();
            await updateMenuForServerLess(menuEnabled);
        }
        if (!sceneStateXML) {
            let sceneData = await SceneStateService.getSceneStateData({
                sceneStateId: sceneStateId
            });
            sceneStateXML = sceneData?.sceneStateXML;
        }
        try {
            await this._loadFeaturesFromXml(sceneStateXML, sceneStateModels, arMarkers);
            const message = "Successfully loaded scene " + stateName;
            VRMessageController.addMessage({text: message, priority: VrMessagePriorityEnum.LOWPRIORITYINFO});
        } catch (error) {
            const message = "Error loading scene " + stateName;
            VRMessageController.addMessage({text: message, priority: VrMessagePriorityEnum.LOWPRIORITYINFO});
            console.log(error);
        }
    }

    async loadModelState(modelId, modelStateName) {
        let modelStateXML = await SceneStateService.getModelStateData({
            modelId: modelId,
            modelStateName: modelStateName
        });

        try {
            const model = modelManager.getModelById(modelId);
            await this._loadFeaturesFromXml(modelStateXML, [model], null, false);
            const message = "Successfully loaded model state " + modelStateName;
            VRMessageController.addMessage({text: message, priority: VrMessagePriorityEnum.LOWPRIORITYINFO});
        } catch (error) {
            const message = "Error loading model state " + modelStateName;
            VRMessageController.addMessage({text: message, priority: VrMessagePriorityEnum.LOWPRIORITYINFO});
            console.log(error);
        }
    }

    checkIfStateIsTheLoadedState(stateId){
        // Initially null during loading in workspace
        if(!stateId){
            return true;
        }

        try {
            const loadedStates = TrainingTreeController._trainingFeature.getActiveTraining().states;
            const isThisStateLoaded = Object.keys(loadedStates).some(key => key == stateId);

            return isThisStateLoaded;
        }catch (e) {
            return false
        }
    }
}

export default new SceneStateController();
